import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaInstagram, FaTripadvisor, FaYoutube } from 'react-icons/fa';

// Styled Components
const FooterSection = styled.footer`
  background-color: #1e3a8a;
  color: white;
  padding: 2rem 0 1rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
`;

const FooterColumn = styled.div``;

const FooterLogo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const FooterDescription = styled.p`
  color: #e5e7eb;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.95rem;
`;

const FooterTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  position: relative;
  padding-bottom: 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: #f59e0b;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.75rem;
  
  a {
    color: #e5e7eb;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-block;
    
    &:hover {
      color: #f59e0b;
      transform: translateX(5px);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f59e0b;
    transform: translateY(-3px);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  text-align: center;
  color: #e5e7eb;
  font-size: 0.875rem;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to handle scrolling to sections on homepage
  const scrollToSection = (sectionId) => {
    // If we're not on the homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/');
      // Need to wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If already on homepage, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  // Function to navigate to a page and scroll to top
  const navigateToPage = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  return (
    <FooterSection>
      <FooterContainer>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>Wular water front resort</FooterLogo>
            <FooterDescription>
              Experience luxury and tranquility on the shores of Wular Lake. 
              Our resort offers a perfect blend of comfort, natural beauty, and 
              exceptional hospitality.
            </FooterDescription>
            <SocialLinks>
              <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <FaFacebookF />
              </SocialIcon>
              <SocialIcon href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <FaTwitter />
              </SocialIcon>
              <SocialIcon href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="https://tripadvisor.com" target="_blank" rel="noopener noreferrer">
                <FaTripadvisor />
              </SocialIcon>
              <SocialIcon href="https://youtube.com" target="_blank" rel="noopener noreferrer">
                <FaYoutube />
              </SocialIcon>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <button onClick={() => navigateToPage('/')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Home</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => navigateToPage('/rooms')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Rooms</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => navigateToPage('/gallery')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Gallery</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => navigateToPage('/booking')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Book Now</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => navigateToPage('/contact')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Contact</button>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Our Services</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <button onClick={() => scrollToSection('rooms')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Accommodation</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => scrollToSection('facilities')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Facilities</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => scrollToSection('services')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Dining</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => scrollToSection('services')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Activities</button>
              </FooterLink>
              <FooterLink>
                <button onClick={() => scrollToSection('services')} style={{ background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', padding: 0, font: 'inherit' }}>Events</button>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterTitle>Contact Info</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <span>Sangritop, Watlab Sopore </span>
                <span> Jammu & Kashmir, India - 193201</span>
              </FooterLink>
              <FooterLink>
                <a href="tel:+916005697823">+91 6005697823</a>
              </FooterLink>
              <FooterLink>
                <a href="mailto:wularwaterfrontresort@gmail.com">wularwaterfrontresort@gmail.com</a>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>
        </FooterContent>
        
        <FooterBottom>
          <p>&copy; {currentYear} Wular water front resort. All rights reserved.<br></br> <br></br> Designed & Developed with ❤️ by Drealence Technologies</p>
        </FooterBottom>
      </FooterContainer>
    </FooterSection>
  );
};

export default Footer;
